import { FC } from 'react';
import { Capacitor } from '@capacitor/core';
import {
	FirebaseAuthentication,
	SignInResult,
} from '@capacitor-firebase/authentication';
import {
	getAuth,
	OAuthProvider,
	signInWithCredential,
	signInWithPopup,
	UserCredential,
} from 'firebase/auth';

import { AppleButton } from '../../molecules/SocialButtons';

import {
	ISocialLoginResult,
	SocialLoginError,
	SocialLoginErrorCodeEnum,
	SocialProviderEnum,
	TPushEvent,
} from '.';

type LoginWithAppleProps = {
	onSignIn: (callback: () => Promise<ISocialLoginResult>) => Promise<void>;
	pushAuthEvent?: TPushEvent;
	isRegistration?: boolean;
};

export const LoginWithApple: FC<LoginWithAppleProps> = ({
	onSignIn,
	pushAuthEvent,
	isRegistration,
}) => {
	const signInWithApple = async () => {
		const auth = getAuth();

		if (pushAuthEvent) {
			pushAuthEvent(
				isRegistration ? 'sign_up_attempt' : 'login_attempt',
				'apple'
			);
		}

		// Web: use Firebase's signInWithPopup directly
		if (!Capacitor.isNativePlatform()) {
			const provider = new OAuthProvider(SocialProviderEnum.APPLE);
			const popupResult = await signInWithPopup(auth, provider);

			if (!popupResult.user) {
				throw new SocialLoginError(SocialLoginErrorCodeEnum.LOGIN_FAILED, {
					cause: 'No user returned from popup',
				});
			}

			if (pushAuthEvent) {
				pushAuthEvent(isRegistration ? 'sign_up' : 'login', 'apple');
			}

			return { user: popupResult.user, providerId: SocialProviderEnum.APPLE };
		}

		// Native: use the Capacitor plugin
		await FirebaseAuthentication.signOut();

		const result: SignInResult = await FirebaseAuthentication.signInWithApple();

		const idToken = result.credential?.idToken;
		const rawNonce = result.credential?.nonce;

		if (!idToken) {
			throw new SocialLoginError(SocialLoginErrorCodeEnum.LOGIN_FAILED, {
				cause: 'Missing idToken',
			});
		}

		const provider = new OAuthProvider(SocialProviderEnum.APPLE);
		const credential = provider.credential({
			idToken,
			rawNonce,
		});

		const signInResult: UserCredential = await signInWithCredential(
			auth,
			credential
		);

		if (!signInResult.user) {
			throw new SocialLoginError(SocialLoginErrorCodeEnum.LOGIN_FAILED, {
				cause: 'No user returned',
			});
		}
		if (pushAuthEvent) {
			pushAuthEvent(isRegistration ? 'sign_up' : 'login', 'apple');
		}
		return { user: signInResult.user, providerId: SocialProviderEnum.APPLE };
	};

	return <AppleButton onClick={() => onSignIn(signInWithApple)} />;
};
